import {Pagination} from 'components/Pagination';
import {isBrowserContext} from 'core/helpers';
import {PageInfo} from 'models';
import React, {MouseEventHandler, useCallback, useMemo} from 'react';
import style from './style.module.scss';
import {DEFAULT_SCROLL_OFFSET, PAGINATION_TYPE} from 'plugins/modules/pagination/constants';
import {TPaginationType} from 'plugins/modules/pagination/types';
import classNames from 'classnames';
import {ButtonNew} from 'components/ButtonNew';

export interface IProps<E extends HTMLElement> extends PageInfo {
    className?: string;
    onChange?: (pageNumber: number, type: TPaginationType) => void;
    isLoading?: boolean;
    scrollTarget?: E | null;
    scrollOffset?: number;
    pluralList?: string[];
    scrollOnShowMore?: boolean;
}

export const ListPagination = <E extends HTMLElement = HTMLElement>({
    className,
    isLoading,
    pageNumber = 1,
    pageSize,
    onChange,
    recordCount,
    scrollTarget,
    scrollOffset = DEFAULT_SCROLL_OFFSET,
    scrollOnShowMore = false,
}: IProps<E>) => {
    const handlePageChangeScroll = useCallback(() => {
        if (!isBrowserContext()) {
            return;
        }

        if (!scrollTarget) {
            window.scrollTo({behavior: 'smooth', top: 0, left: window.scrollX});
            return;
        }

        const dimension = scrollTarget.getBoundingClientRect();

        window.scrollTo({
            behavior: 'smooth',
            left: window.scrollX,
            top: dimension.top - scrollOffset + window.scrollY,
        });
    }, [scrollOffset, scrollTarget]);

    const handleShowMoreClick = useCallback<MouseEventHandler<HTMLButtonElement>>(() => {
        if (onChange) {
            onChange(pageNumber + 1, PAGINATION_TYPE.ACCUMULATIVE);

            if (scrollOnShowMore) {
                handlePageChangeScroll();
            }
        }
    }, [handlePageChangeScroll, onChange, pageNumber, scrollOnShowMore]);

    const handlePageClick = useCallback(
        (page: number) => {
            handlePageChangeScroll();
            if (onChange) {
                onChange(page, 'auto');
            }
        },
        [handlePageChangeScroll, onChange],
    );

    const totalPages = useMemo<number>(() => {
        if (!recordCount) {
            return 1;
        }

        if (!pageSize) {
            return 1;
        }

        return Math.ceil(recordCount / pageSize);
    }, [pageSize, recordCount]);

    const isShowMoreButtonDisabled = useMemo<boolean>(() => {
        return Boolean(pageSize * pageNumber >= recordCount || isLoading);
    }, [isLoading, pageNumber, pageSize, recordCount]);

    return (
        <div className={classNames(style.paginationContainer, className)}>
            <div className={style.paginationPages}>
                {recordCount > pageSize && (
                    <Pagination
                        className={style.listLayoutPaginationContainer}
                        currentPage={pageNumber}
                        onChange={handlePageClick}
                        totalPages={totalPages}
                    />
                )}
            </div>
            <div className={style.listLayoutShowMoreContainer}>
                <div className={style.listLayoutPaginationProgressContainer}>
                    {recordCount > pageSize && !isShowMoreButtonDisabled && (
                        <ButtonNew
                            isLoading={isLoading}
                            onClick={handleShowMoreClick}
                            variant={'large'}
                            theme={'white'}
                        >
                            Показать еще
                        </ButtonNew>
                    )}
                </div>
            </div>
            <div className={style.offset} />
        </div>
    );
};
